'use client';

import { useMemo } from 'react';
import Image from 'next/image';
import { useBuyContext } from '../../state/BuyContext';
import { useBuySelection } from '../../state/useBuySelection';
import { resolve, type SwatchCardConfig } from '../../state/cardSchema';
import { Section } from '../Section';
import styles from './SwatchCard.module.css';

export type SwatchCardProps = {
  card: SwatchCardConfig;
};

export const SwatchCard = ({ card }: SwatchCardProps) => {
  const { resolveCtx } = useBuyContext();
  const { selected, select } = useBuySelection(card.id);

  const activeLabel = useMemo(() => {
    const opt = card.options.find((o) => o.id === selected);
    return opt?.label;
  }, [card.options, selected]);

  return (
    <Section id={card.id} title={card.title} subtitle={card.subtitle} info={card.info}>
      {activeLabel ? <p className={styles.activeLabel}>{activeLabel}</p> : null}
      <div className={styles.grid} role="radiogroup" aria-label={typeof card.title === 'string' ? card.title : card.id}>
        {card.options.map((option) => {
          const disabled = !!resolve(option.disabled, resolveCtx);
          const isActive = selected === option.id;
          return (
            <button
              key={option.id}
              type="button"
              className={styles.swatchButton}
              data-active={isActive ? 'true' : 'false'}
              onClick={() => select(option.id)}
              disabled={disabled}
              role="radio"
              aria-checked={isActive}
              aria-label={option.label}
            >
              <Image
                src={option.image}
                alt={option.label}
                width={48}
                height={48}
              />
              {option.badge ? <span className={styles.badge}>{option.badge}</span> : null}
            </button>
          );
        })}
      </div>
    </Section>
  );
};
